import { Component } from 'preact'
import s from './summary-section.sass'
import gs from '../generic-sign-up.sass'

import CollapsibleSection from '../collapsible-section'
import Separator from '../separator'
import RowSelect from '../row-select'
import PriceSummary from '../price-summary'
import ActionButton from '../../action-button'
import ErrorList from '../error-list'
import { connect } from 'unistore/preact'
import { getFilteredProducts } from './selectors'
import { daysTillThen, formattedDate, stringDate } from '../../../helpers/dates'
import { checkoutConfirmPayment } from '../../../helpers/analytics'
import WarningMessage from '../warning-message'
import { PAYMENT_METHODS, PAYMENT_METHODS_DICTIONARY } from '../constants'

const getPriceKey = (paymentMethod) => paymentMethod === PAYMENT_METHODS.rcp
  ? `price${paymentMethod.toUpperCase()}` // priceRCP
  : `price${paymentMethod[0].toUpperCase() + paymentMethod.slice(1)}` // priceCard, priceSwish, priceAutogiro

const SummaryRow = ({ label, extra, price }) => (
  <RowSelect nonInteractive>
    <div className={s.summaryRow}>
      <div>{label}</div>
      <div>{extra}</div>
      <div>{price}</div>
    </div>
  </RowSelect>
)

class SummarySection extends Component {
  priceOrFree (value, suffix) {
    return parseInt(value)
      ? value + suffix
      : 'Ingår'
  }

  getPriceEntries () {
    const product = this.props.products[0]
    const priceKey = getPriceKey(product.paymentMethod)
    const startEntries = [ // Starting fee are all the same
      {
        label: `Startavgift`,
        price: this.priceOrFree(product[priceKey].additionalFees.startingFeeSEK, 'kr')
      }
    ]

    // entry card option
    if (this.props.paymentInfo.membershipCardDelivery !== 'skipDelivery') {
      startEntries.push({
        label: 'Gymkort',
        price: this.priceOrFree(product[priceKey].additionalFees.cardFeeSEK, 'kr')
      })
    }

    switch (product.paymentMethod) {
      case PAYMENT_METHODS.autogiro:
        const initialPayments = product.priceAutogiro.initialPayments.map(p => ({
          label: `Medlemskap (Perioden, ${stringDate(p.from)} - ${stringDate(p.to)})`,
          price: this.priceOrFree(p.priceSEK, 'kr')
        }))

        return [...initialPayments, ...startEntries]

      case PAYMENT_METHODS.rcp:
        const firstPayment = []
        if (product[priceKey].membershipCostFirstPayment) {
          firstPayment.push({
            label: `Medlemskap (Månadsvis)`,
            price: `${product[priceKey].membershipCostFirstPaymentSEK} kr/mån`
          })
        }

        return [...firstPayment, ...startEntries]

      case PAYMENT_METHODS.swish:
      case PAYMENT_METHODS.card:
        return [
          {
            label: `Medlemskap`,
            price: this.priceOrFree(product[priceKey].priceSEK, 'kr')
          },
          ...startEntries
        ]

      default:
        return []
    }
  }

  calculateHeroPrice () {
    const product = this.props.products[0]
    const priceKey = getPriceKey(product.paymentMethod)
    const startingFee = product[priceKey].additionalFees.startingFee
    const keyCardFee = this.props.paymentInfo.membershipCardDelivery !== 'skipDelivery'
      ? product[priceKey].additionalFees.cardFee
      : 0

    switch (product.paymentMethod) {
      case PAYMENT_METHODS.autogiro:
        const initialPayments = product[priceKey].initialPayments.reduce((result, payment) => (result + payment.price), 0)
        return (
          initialPayments +
            startingFee +
            keyCardFee
        ) / 100

      case PAYMENT_METHODS.rcp:
        return (
          product[priceKey].membershipCostFirstPayment +
            startingFee +
            keyCardFee
        ) / 100

      case PAYMENT_METHODS.swish:
      case PAYMENT_METHODS.card:
        return (
          product[priceKey].price +
          startingFee +
          keyCardFee
        ) / 100
    }
  }

  handleOnSubmit () {
    this.props.onClick()
    checkoutConfirmPayment(this.props.paymentInfo.allowEmailMarketing)
  }

  render ({
    title,
    paymentInfo,
    skipCard,
    products,
    hasAccepted,
    onInput,
    onClick,
    error,
    isSubmitting,
    ...rest
  }) {
    const heroPrice = products && products.length === 1
      ? this.calculateHeroPrice()
      : '-'

    const product = products && products.length === 1 && products[0]
    let endDate
    let daysRemaining = 0

    if (product) {
      endDate = new Date(product.startDate)
      endDate.setSeconds(-1)
      daysRemaining = daysTillThen(endDate)
    }

    return (
      <CollapsibleSection
        className={s.summarySection}
        {...rest}
      >
        <Separator className={gs.signUpSeparator} />
        <div className={gs.content}>
          <h2>{title}</h2>
          <div className={s.summaryList}>
            {product && this.getPriceEntries().filter(v => v).map((entry, key) => (
              <SummaryRow key={key} label={entry.label} price={entry.price} />
            ))}
            <SummaryRow label='Försäkring' extra={<a href='/regler-villkor-policy' target='_blank' rel='noopener' className={s.legal}>Läs våra villkor</a>} price='Ingår' />
            <PriceSummary label={'Total kostnad'} amount={`${heroPrice}kr`}>
              {error && <ErrorList message={error.message} errorFields={error.fields} />}
              <ActionButton disabled={!hasAccepted && 'disabled'} label={paymentInfo.paymentMethod ? `Betala med ${PAYMENT_METHODS_DICTIONARY[paymentInfo.paymentMethod]}` : 'Betala'} onClick={() => this.handleOnSubmit()} loading={isSubmitting} />
            </PriceSummary>
            {daysRemaining > 0 &&
              <WarningMessage
                message={`Du har redan ett medlemskap som är aktivt fram till ${formattedDate(endDate)} (${daysRemaining} dagar kvar).\nHar du ett kontantmedlemskap eller ett redan uppsagt löpande medlemskap kommer ditt nya börja gälla dagen efter ditt nuvarande medlemskap går ut.\nHar du ett löpande medlemskap behöver du kontakta oss för att säga upp det.`}
              />}
          </div>
        </div>
      </CollapsibleSection>
    )
  }
}

export const selectors = (state) => {
  const { paymentInfo } = state
  return {
    paymentInfo,
    products: getFilteredProducts(state)
  }
}

export default connect(selectors)(SummarySection)



// WEBPACK FOOTER //
// ./components/signup-flow/step-payment/summary-section.js